import React, { useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { motion, Variants } from "framer-motion";
import { Home, Users, Video, MessageSquare, PenSquare } from "lucide-react";
import Sidebar from './Sidebar';
import RightPanel from './RightPanel';
import { useThemeContext } from './ThemeWrapper';

type ChapterData = {
  id: number;
  title: string;
  subtitle: string;
  image: string;
};

const chapters: ChapterData[] = [
  { id: 1, title: "はじめまして", subtitle: "Nice to meet you", image: `${process.env.PUBLIC_URL}/learnimage/neko.png` },
  { id: 2, title: "こんにちは", subtitle: "Greetings", image: `${process.env.PUBLIC_URL}/learnimage/elearning.png` },
  { id: 3, title: "ありがとう", subtitle: "Thank you", image: `${process.env.PUBLIC_URL}/learnimage/thank-you.png` },
  { id: 4, title: "ごめんなさい", subtitle: "Apologies", image: `${process.env.PUBLIC_URL}/learnimage/sorry.png` },
  { id: 5, title: "いくつですか", subtitle: "How many?", image: `${process.env.PUBLIC_URL}/learnimage/confused.png` },
  { id: 6, title: "どこですか", subtitle: "Where is it?", image: `${process.env.PUBLIC_URL}/learnimage/where.png` },
  { id: 7, title: "なにがすき", subtitle: "What do you like?", image: `${process.env.PUBLIC_URL}/learnimage/tiger.png` },
  { id: 8, title: "だれですか", subtitle: "Who is it?", image: `${process.env.PUBLIC_URL}/learnimage/curiosity.png` },
  { id: 9, title: "おわり", subtitle: "The End", image: `${process.env.PUBLIC_URL}/learnimage/elearning (1).png` },
];

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 50, scale: 0.8 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 100, damping: 10 } },
};

interface ChapterSelectionProps {
  navigate: ReturnType<typeof useNavigate>;
  isDarkTheme: boolean;
}

const ChapterSelection: React.FC<ChapterSelectionProps> = ({ navigate, isDarkTheme }) => {
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);

  const theme = {
    containerBg: isDarkTheme ? 'bg-zinc-900' : 'bg-white',
    cardBg: isDarkTheme ? 'bg-zinc-700' : 'bg-yellow-100',
    cardBorder: isDarkTheme ? 'border-yellow-400' : 'border-yellow-500',
    cardText: isDarkTheme ? 'text-yellow-300' : 'text-yellow-700',
    subtitleText: isDarkTheme ? 'text-yellow-400' : 'text-yellow-600',
    defaultBg: isDarkTheme ? 'bg-zinc-900 border-gray-700 hover:shadow-lg' : 'bg-white border-gray-200 hover:shadow-xl',
    defaultText: isDarkTheme ? 'text-white' : 'text-black',
  };

  return (
    <div className="flex flex-col items-center w-full px-4 sm:px-6 lg:px-8">
      <motion.h1
        className={`text-3xl sm:text-4xl font-extrabold mb-8 p-4 rounded-xl relative shadow-md text-center ${
          isDarkTheme ? 'bg-zinc-800 text-yellow-400 shadow-lg' : 'bg-yellow-50 text-yellow-700 shadow-md'
        }`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        START HERE
        <span
          className={`absolute left-1/2 -bottom-4 w-0 h-0 border-x-8 border-x-transparent border-t-8 transform -translate-x-1/2 ${
            isDarkTheme ? 'border-t-zinc-800' : 'border-t-yellow-50'
          }`}
        ></span>
      </motion.h1>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full max-w-6xl"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {chapters.map(chapter => {
          const isSelected = chapter.id === selectedChapter;
          const cardBgClass = isSelected ? theme.cardBg : theme.defaultBg;
          const titleClass = isSelected ? theme.cardText : theme.defaultText;
          const subtitleClass = isSelected ? theme.subtitleText : (isDarkTheme ? 'text-gray-300' : 'text-gray-700');
          const borderClass = isSelected ? theme.cardBorder : 'border-gray-200';
          return (
            <motion.div
              key={chapter.id}
              onClick={() => setSelectedChapter(chapter.id)}
              className={`flex flex-col items-center p-4 sm:p-6 rounded-3xl shadow-lg cursor-pointer transition-all duration-200 overflow-hidden ${cardBgClass} border-2 ${borderClass}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              variants={itemVariants}
            >
              <div className={`text-xl font-bold mb-2 ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'}`}>
                {chapter.id}.
              </div>
              <img
                src={chapter.image}
                alt={chapter.title}
                className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover mb-4 ${
                  isSelected ? (isDarkTheme ? 'border-gray-600' : 'border-yellow-50') : (isDarkTheme ? 'border-gray-700' : 'border-gray-200')
                }`}
              />
              <h3 className={`text-2xl sm:text-3xl font-bold mb-1 ${titleClass}`}>{chapter.title}</h3>
              <p className={`text-base sm:text-lg ${subtitleClass}`}>{chapter.subtitle}</p>

              {isSelected && (
                <motion.button
                  onClick={() => navigate(`/chapter/${chapter.id}`)}
                  className={`mt-4 font-bold py-2 px-6 rounded-xl shadow-lg w-full ${
                    isDarkTheme ? 'bg-pink-600 hover:bg-pink-700 text-white' : 'bg-pink-500 hover:bg-pink-600 text-white'
                  }`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ type: "spring", stiffness: 200, damping: 20 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Start
                </motion.button>
              )}
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export const LearnPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkTheme } = useThemeContext();

  return (
    <div className={`flex min-h-screen font-inter ${isDarkTheme ? 'bg-black text-white' : 'bg-white text-black'}`}>
      <Sidebar />
      <main className={`flex-1 p-8 pb-20 md:p-12 overflow-y-auto ${isDarkTheme ? 'bg-black text-white' : 'bg-white text-black'}`}>
        <ChapterSelection navigate={navigate} isDarkTheme={isDarkTheme} />
      </main>
      <RightPanel />
    </div>
  );
};
